import type { OfflineVisits } from '@app-worker/shared/visit/offlineVisits/OfflineVisits';

// `public/offline.html` reads it too; `offlineVisitsContract.spec.ts` holds the two together.
export const OFFLINE_VISITS_STORAGE_KEY = 'workerOfflineVisits';

export type OfflineVisitsStorage = {
  write: (offlineVisits: OfflineVisits) => void;
  clear: () => void;
};

type WritableStorage = Pick<Storage, 'setItem' | 'removeItem'>;

/**
 * Local, not session, storage: the offline page is a fresh load, often after
 * iOS dropped the tab.
 *
 * Every call is wrapped: `localStorage` is missing on the server, throws when
 * site data is blocked, and `setItem` throws when full. A lost save only means
 * the offline page has nothing to show. The storage is a getter so a spec can
 * hand in one that throws.
 */
export const createOfflineVisitsStorage = (
  readStorage: () => WritableStorage = readLocalStorage,
): OfflineVisitsStorage => ({
  write: function writeOfflineVisits(offlineVisits) {
    try {
      readStorage().setItem(
        OFFLINE_VISITS_STORAGE_KEY,
        JSON.stringify(offlineVisits),
      );
    } catch {
      // the offline page falls back to "no visits saved"
    }
  },
  clear: function clearOfflineVisits() {
    try {
      readStorage().removeItem(OFFLINE_VISITS_STORAGE_KEY);
    } catch {
      // never let logout throw
    }
  },
});

function readLocalStorage(): WritableStorage {
  return window.localStorage;
}
